import classNames from 'classnames';
import { useEffect, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import IndividualSelector from '@/components/molecules/individual-selector/individual-selector';
import MultipleSelector from '@/components/molecules/multiple-selector/multiple-selector';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import useMutationPatchPreBookingProduct from '@/integrations/booking/pre-booking/products/patch-product/patch-product.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import {
  getSelectionChangesMultipleChoice,
  getSelectionChangesSingleChoice,
} from '../booking-extra-utils/booking-extra-utils';
import styles from './booking-extra-select-modal.module.scss';

export interface BookingExtraSelectModalProps {
  /** The category IDs */
  categoryIds: number[];
  /** The generic product choice type */
  choiceType?: Content.Components['ProductModuleChoiceType'];
  /** The class name to apply */
  className?: string;
  /** Indicates whether the extra has distinction for homebound and outbound travels */
  containsOutHomeProducts?: boolean;
  /** The description of the modal */
  description?: string;
  /** The generic product */
  genericProduct?: Content.Components['GenericProductModuleDto']['genericProduct'];
  /** The headline of the modal */
  headline?: string;
  /** Identifiers */
  identifiers: {
    bookingStateId: string;
    id: string;
    pageId: string;
  };
  /** Invalidation keys for query cache */
  invalidationKeys: QueryId[];
  /** Indicates whether the modal is displayed in a webview */
  isInWebview?: boolean;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the close event. */
  onClose: () => void;
  /** The passengers to display in the modal */
  passengers: Content.Components['GenericProductPassengerDto'][];
}

const BookingExtraSelectModal = ({
  categoryIds = [],
  choiceType = 'single',
  className,
  containsOutHomeProducts = false,
  description,
  genericProduct,
  headline,
  identifiers,
  invalidationKeys = [],
  isInWebview = false,
  isOpen = false,
  onClose,
  passengers = [],
}: BookingExtraSelectModalProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updatedPassengers, setUpdatedPassengers] = useState<
    Content.Components['GenericProductPassengerDto'][]
  >([]);

  const {
    data: dataPatchPreBookingProduct,
    isLoading: isMutatingPatchPreBookingProduct,
    mutateAsync: mutateAsyncPatchPreBookingProduct,
  } = useMutationPatchPreBookingProduct();

  const handleOnClickSave = async (
    passengers: Content.Components['GenericProductPassengerDto'][],
  ) => {
    if (genericProduct?.choiceType === 'single') {
      const patchProductBody = getSelectionChangesSingleChoice(
        passengers,
        genericProduct?.passengersWithProductSelected ?? [],
        categoryIds,
      );

      if (!patchProductBody) return;

      await mutateAsyncPatchPreBookingProduct({
        brand,
        data: patchProductBody,
        invalidationKeys: invalidationKeys,
        parameters: identifiers,
      });
    }

    if (genericProduct?.choiceType === 'multiple') {
      const patchProductBody = getSelectionChangesMultipleChoice(
        passengers,
        genericProduct?.passengersWithProductSelected ?? [],
        categoryIds,
      );

      if (!patchProductBody) return;

      await mutateAsyncPatchPreBookingProduct({
        brand,
        data: patchProductBody,
        invalidationKeys: invalidationKeys,
        parameters: identifiers,
      });
    }

    if (isInWebview && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          status: 'success',
        }),
      );

      return;
    }
  };

  useEffect(() => {
    if (!dataPatchPreBookingProduct) return;

    if (!dataPatchPreBookingProduct.result) {
      setErrorMessage(dataPatchPreBookingProduct.message ?? null);
    } else {
      setErrorMessage(null);
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPatchPreBookingProduct]);

  useEffect(() => {
    setUpdatedPassengers(passengers);
  }, [passengers]);

  const handleSingleSelect = (
    uniquePaxHash: string,
    direction: Content.Components['ProductTransportDirection'] = 'default',
  ) => {
    const updated = updatedPassengers.map(passenger => {
      if (passenger.passenger.uniquePaxHash === uniquePaxHash) {
        const updatedRelevantProducts =
          passenger.relevantPassengerBookingProducts.map(product =>
            product.direction === direction
              ? { ...product, isSelected: !product.isSelected }
              : product,
          );

        return {
          ...passenger,
          isSelected: updatedRelevantProducts.some(
            product => product.isSelected,
          ),
          relevantPassengerBookingProducts: updatedRelevantProducts,
        };
      }

      return passenger;
    });

    setUpdatedPassengers(updated);
  };

  const handleMultipleSelect = (
    selectedValue: string,
    uniquePaxHash: string,
    direction: Content.Components['ProductTransportDirection'] = 'default',
  ) => {
    const updated = updatedPassengers.map(passenger => {
      if (passenger.passenger.uniquePaxHash === uniquePaxHash) {
        const updatedRelevantProducts =
          passenger.relevantPassengerBookingProducts.map(product =>
            product.direction === direction
              ? { ...product, isSelected: product.bindId === selectedValue }
              : product,
          );

        return {
          ...passenger,
          isSelected: updatedRelevantProducts.some(
            product => product.isSelected,
          ),
          relevantPassengerBookingProducts: updatedRelevantProducts,
        };
      }

      return passenger;
    });

    setUpdatedPassengers(updated);
  };

  useEffect(() => {
    setUpdatedPassengers(passengers);
  }, [isOpen, passengers]);

  const renderSingleSelect = () => {
    if (containsOutHomeProducts) {
      return (
        <div className={styles.directionWrapper}>
          <div className={styles.singleOptionsContainer}>
            <div className={styles.outboundHeader}>
              <Icon name="Luggage" size={16} />

              <Typography tagStyle="bodyMediumBold">
                {t('items.modules.bookingFlow.outbound')}
              </Typography>
            </div>

            <div className={styles.singleOptionsWrapper}>
              {updatedPassengers.map(passenger => {
                return (
                  <IndividualSelector
                    key={`outbound-single-${passenger.passenger.uniquePaxHash}`}
                    className={styles.singleOptionItem}
                    direction={'out'}
                    onClick={() =>
                      handleSingleSelect(
                        passenger.passenger.uniquePaxHash,
                        'out',
                      )
                    }
                    passenger={passenger}
                  />
                );
              })}
            </div>
          </div>

          <div className={styles.divider}></div>

          <div className={styles.singleOptionsContainer}>
            <div className={styles.homeboundHeader}>
              <Icon name="Luggage" size={16} />

              <Typography tagStyle="bodyMediumBold">
                {t('items.modules.bookingFlow.homebound')}
              </Typography>
            </div>

            <div className={styles.singleOptionsWrapper}>
              {updatedPassengers.map(passenger => {
                return (
                  <IndividualSelector
                    key={`homebound-single-${passenger.passenger.uniquePaxHash}`}
                    className={styles.singleOptionItem}
                    direction={'home'}
                    onClick={() =>
                      handleSingleSelect(
                        passenger.passenger.uniquePaxHash,
                        'home',
                      )
                    }
                    passenger={passenger}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.singleOptionsContainer}>
        <Typography tagStyle="bodyMediumBold">
          {t('items.modules.bookingFlow.select')}
        </Typography>

        <div className={styles.singleOptionsWrapper}>
          {updatedPassengers.map(passenger => {
            return (
              <IndividualSelector
                key={passenger.passenger.uniquePaxHash}
                className={styles.singleOptionItem}
                onClick={() =>
                  handleSingleSelect(passenger.passenger.uniquePaxHash)
                }
                passenger={passenger}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderMultiSelect = () => {
    if (containsOutHomeProducts) {
      return (
        <div className={styles.directionWrapper}>
          <div className={styles.outbound}>
            <div className={styles.outboundHeader}>
              <Icon name="Luggage" size={16} />

              <Typography tagStyle="bodyMediumBold">
                {t('items.modules.bookingFlow.outbound')}
              </Typography>
            </div>

            <div className={styles.multipleOptionsWrapper}>
              {updatedPassengers.map(passenger => {
                return (
                  <MultipleSelector
                    key={`outbound-multiple-${passenger.passenger.uniquePaxHash}`}
                    containsOutHomeProducts={containsOutHomeProducts}
                    direction={'out'}
                    onChange={selectedValue =>
                      handleMultipleSelect(
                        selectedValue,
                        passenger.passenger.uniquePaxHash,
                        'out',
                      )
                    }
                    passenger={passenger}
                  />
                );
              })}
            </div>
          </div>

          <div className={styles.divider}></div>

          <div className={styles.homebound}>
            <div className={styles.homeboundHeader}>
              <Icon name="Luggage" size={16} />

              <Typography tagStyle="bodyMediumBold">
                {t('items.modules.bookingFlow.homebound')}
              </Typography>
            </div>

            <div className={styles.multipleOptionsWrapper}>
              {updatedPassengers.map(passenger => {
                return (
                  <MultipleSelector
                    key={`homebound-multiple-${passenger.passenger.uniquePaxHash}`}
                    containsOutHomeProducts={containsOutHomeProducts}
                    direction={'home'}
                    onChange={selectedValue =>
                      handleMultipleSelect(
                        selectedValue,
                        passenger.passenger.uniquePaxHash,
                        'home',
                      )
                    }
                    passenger={passenger}
                  />
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.multipleOptionsWrapper}>
        {updatedPassengers.map(passenger => {
          return (
            <MultipleSelector
              key={passenger.passenger.uniquePaxHash}
              onChange={selectedValue =>
                handleMultipleSelect(
                  selectedValue,
                  passenger.passenger.uniquePaxHash,
                )
              }
              passenger={passenger}
            />
          );
        })}
      </div>
    );
  };

  const handleOnClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const renderContent = (): React.ReactNode => {
    return (
      <div className={styles.contentWrapper}>
        <div className={styles.topSection}>
          {!isInWebview && (
            <Typography className={styles.headline} tagStyle="headingMedium">
              {headline}
            </Typography>
          )}

          {description && (
            <RichText className={styles.description} text={description} />
          )}

          {errorMessage && (
            <AlertBanner text={errorMessage} variant="warning" />
          )}

          {choiceType === 'single' && renderSingleSelect()}

          {choiceType === 'multiple' && renderMultiSelect()}
        </div>

        <div className={styles.bottomSection}>
          <Button
            className={styles.cta}
            onClick={onClose}
            text={t('items.common.words.cancel')}
            variant="secondary"
          />

          <Button
            className={styles.cta}
            isLoading={isMutatingPatchPreBookingProduct}
            onClick={() => handleOnClickSave(updatedPassengers)}
            text={t('items.modules.bookingFlow.save')}
            variant="primary"
          />
        </div>
      </div>
    );
  };

  if (isInWebview) {
    return (
      <div className={classNames(styles.component, styles.webviewWrapper)}>
        {renderContent()}
      </div>
    );
  }

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={handleOnClose}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={classNames(styles.lightboxInnerWrapper)}>
        {renderContent()}
      </div>
    </Lightbox>
  );
};

BookingExtraSelectModal.displayName = 'BookingExtraSelectModal';

export default BookingExtraSelectModal;
